/**
 * toReservationDetailView — pure DTO → view mapper for the
 * ReservationDetailView feature (SCRUM-335).
 *
 * The detail feature never consumes the raw `ReservationDetailDto`: it
 * consumes a `ReservationDetailView` computed here. Keeping the projection
 * PURE (no hooks, no I/O, no wall-clock read) makes it unit-testable in
 * isolation and keeps the feature a thin renderer — the exact posture of
 * `to-reservation-row-view.ts` (SCRUM-334).
 *
 * The night arithmetic (`nightsBetween`) and the processor→label map
 * (`sourceLabelFor`) are IMPORTED from `to-reservation-row-view.ts` — the
 * SCRUM-334 helpers are reused, never duplicated. `glyphKind` uses the
 * shipped `glyphKindFor` from the Tier-0 `TypeIconBadge` primitive.
 *
 * Computed fields:
 *   - `nights`          — whole calendar days (UTC-midnight epoch diff).
 *   - `modified`        — `currentSnapshotVersion != null && > 1` (the
 *     "charges re-frozen after create" pencil signal; NOT OCC `version`).
 *   - `glyphKind`       — via `glyphKindFor(stayKind, reservationTypeKey,
 *     processor)`; `processor` upgrades a paying guest to `channelGuest`.
 *   - `sourceLabel`     — via the shipped `sourceLabelFor`.
 *   - `typeDisplayName` — resolved from `typeNameById`; "Untyped" for a
 *     null key; neutral "—" for an unresolved / disabled key OR when no
 *     `typeNameById` is provided (never a raw UPPER_SNAKE key in copy).
 *   - `propertyName`    — `nameById.get(propertyId)`, short-id fallback.
 *   - `guestsLabel`     — `String(guestCount)`, or "—" when null.
 *
 * Money (`grandTotalCents`, `balanceDueCents`) is passed through UNCHANGED
 * — never `/100`, never null→0 coercion. The feature formats via
 * `formatCents` at the display boundary.
 */

import type { ReservationDetailDto } from './types.js';
import { glyphKindFor, type TypeGlyphKind } from '../_components/TypeIconBadge.js';
import { nightsBetween, sourceLabelFor } from './to-reservation-row-view.js';

/** Neutral placeholder for an unresolved / absent value in copy. */
const NEUTRAL = '—';

/** The view the detail feature renders. */
export interface ReservationDetailView {
  /** Stable reservation id (mono subtitle + lifecycle-op target). */
  readonly id: string;
  /** Property id (unresolved fallback source for `propertyName`). */
  readonly propertyId: string;
  /** Resolved property display name, or a short `propertyId` fallback. */
  readonly propertyName: string;
  /** Guest display name (PII-safe short name; null → guest-less). */
  readonly guestDisplayName: string | null;
  /** Wire FSM status (`QUO | HOLD | BKD | CI | CO | CXL | HIST`). */
  readonly status: string;
  /** Opaque reservation-type key (`null` = typeless manual create). */
  readonly reservationTypeKey: string | null;
  /** Resolved type display name ("Untyped" / "—" / catalog name). */
  readonly typeDisplayName: string;
  /** Half-open stay window (calendar dates, `YYYY-MM-DD`). */
  readonly arrivalDate: string;
  readonly departureDate: string;
  /** Whole nights (departure − arrival, calendar days). */
  readonly nights: number;
  /** `true` when charges were re-frozen after create (snapshotVersion>1). */
  readonly modified: boolean;
  /** TypeIconBadge glyph bucket (stayKind + processor channel upgrade). */
  readonly glyphKind: TypeGlyphKind;
  /** Human "Source" label (known processor, or stayKind-derived). */
  readonly sourceLabel: string;
  /** Honest guest count label ("—" when null; no A/C/P split). */
  readonly guestsLabel: string;
  /** DISPLAY-ONLY Cents — passthrough; `null` for legacy pre-273 rows. */
  readonly grandTotalCents: number | null;
  /** DISPLAY-ONLY Cents — passthrough (negative = credit owed to guest). */
  readonly balanceDueCents: number | null;
}

/**
 * Short `propertyId` fallback for an unresolved name — the first segment of
 * the UUID, never an empty string (a silent-failure smell).
 */
function shortPropertyId(propertyId: string): string {
  const head = propertyId.split('-')[0] ?? propertyId;
  return head.length > 0 ? head : propertyId;
}

/**
 * Resolves the type display name for copy:
 *   - null key                       → "Untyped" (typeless manual create)
 *   - key + resolved in typeNameById → the catalog display name
 *   - key + unresolved / no catalog  → neutral "—" (never the raw key)
 */
function typeDisplayNameFor(
  reservationTypeKey: string | null,
  typeNameById?: ReadonlyMap<string, string>,
): string {
  if (reservationTypeKey === null) return 'Untyped';
  const resolved = typeNameById?.get(reservationTypeKey);
  return resolved ?? NEUTRAL;
}

export function toReservationDetailView(
  dto: ReservationDetailDto,
  nameById: ReadonlyMap<string, string>,
  typeNameById?: ReadonlyMap<string, string>,
): ReservationDetailView {
  const modified =
    dto.currentSnapshotVersion !== null &&
    dto.currentSnapshotVersion !== undefined &&
    dto.currentSnapshotVersion > 1;

  return {
    id: dto.id,
    propertyId: dto.propertyId,
    propertyName: nameById.get(dto.propertyId) ?? shortPropertyId(dto.propertyId),
    guestDisplayName: dto.guestDisplayName,
    status: dto.status,
    reservationTypeKey: dto.reservationTypeKey,
    typeDisplayName: typeDisplayNameFor(dto.reservationTypeKey, typeNameById),
    arrivalDate: dto.arrivalDate,
    departureDate: dto.departureDate,
    nights: nightsBetween(dto.arrivalDate, dto.departureDate),
    modified,
    // `processor` feeds the `bookingChannel` param → channel upgrade.
    glyphKind: glyphKindFor(dto.stayKind, dto.reservationTypeKey, dto.processor),
    sourceLabel: sourceLabelFor(dto.processor, dto.stayKind),
    guestsLabel: dto.guestCount === null ? NEUTRAL : String(dto.guestCount),
    // Money passthrough — never coerce null/zero, never /100.
    grandTotalCents: dto.grandTotalCents,
    balanceDueCents: dto.balanceDueCents,
  };
}
